import React from "react";
import { useExcursion } from "contexts/api";
import {
  SMS_GLOBAL_ERROR_CODES,
  type SendExcursionSMSResponse,
} from "api/smsTemplates/SMSTemplate.typed";
import type { ExcursionSmsSendGroup, SMSRecipient } from "../display/ExcursionSmsModal";

export type SmsDispatchStatus =
  | "pending"
  | "sending"
  | "accepted"
  | "moderated"
  | "blacklisted"
  | "invalid"
  | "not_allowed"
  | "flood"
  | "no_phone"
  | "failed"
  | "cancelled";

export type SmsDispatchItem = {
  key: string;
  excursionId: number;
  groupLabel: string;
  recipient: SMSRecipient;
  status: SmsDispatchStatus;
  error?: string;
};

export type SmsDispatchPayload = {
  template_id?: number;
  message?: string;
};

export type SmsDispatchCounters = {
  total: number;
  done: number;
  sent: number;
  refused: number;
  failed: number;
  retryable: number;
};

const SMS_DISPATCH_CONCURRENCY = 3;

const RETRYABLE_STATUSES: ReadonlySet<SmsDispatchStatus> = new Set(["failed", "cancelled"]);
const SENT_STATUSES: ReadonlySet<SmsDispatchStatus> = new Set(["accepted", "moderated"]);
const REFUSED_STATUSES: ReadonlySet<SmsDispatchStatus> = new Set([
  "blacklisted",
  "invalid",
  "not_allowed",
  "flood",
  "no_phone",
]);

export function createSmsDispatchItem(
  group: ExcursionSmsSendGroup,
  recipient: SMSRecipient,
): SmsDispatchItem {
  return {
    key: `${group.excursionId}-${recipient.kind}-${recipient.id}`,
    excursionId: group.excursionId,
    groupLabel: group.label,
    recipient,
    status: "pending",
  };
}

export function isRetryableSmsStatus(status: SmsDispatchStatus): boolean {
  return RETRYABLE_STATUSES.has(status);
}

function outcomeFromResult(
  result: SendExcursionSMSResponse,
): Pick<SmsDispatchItem, "status" | "error"> {
  if (result.sent > 0) return { status: result.moderated ? "moderated" : "accepted" };
  if ((result.blacklisted ?? 0) > 0) return { status: "blacklisted" };
  if ((result.invalid ?? 0) > 0) return { status: "invalid" };
  if ((result.not_allowed ?? 0) > 0) return { status: "not_allowed" };
  if ((result.flood ?? 0) > 0) return { status: "flood" };
  if (result.skipped_no_phone > 0) return { status: "no_phone" };

  return { status: "failed", error: "Non envoyé par le fournisseur" };
}

function isGlobalErrorCode(code: string | undefined): boolean {
  return !!code && (SMS_GLOBAL_ERROR_CODES as readonly string[]).includes(code);
}

export function useExcursionSmsDispatch() {
  const { sendExcursionSMS } = useExcursion();
  const [items, setItems] = React.useState<SmsDispatchItem[]>([]);
  const [running, setRunning] = React.useState(false);
  const [globalError, setGlobalError] = React.useState<string | null>(null);
  const runningRef = React.useRef(false);
  const payloadRef = React.useRef<SmsDispatchPayload>({});

  const patchItem = React.useCallback((key: string, next: Partial<SmsDispatchItem>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...next } : it)));
  }, []);

  const run = React.useCallback(
    async (queue: SmsDispatchItem[], payload: SmsDispatchPayload) => {
      if (runningRef.current || queue.length === 0) return;
      runningRef.current = true;
      setRunning(true);
      setGlobalError(null);

      // Shared between workers; a global provider error stops further dequeues.
      const state = { cursor: 0, abortError: null as string | null };

      const worker = async () => {
        while (state.abortError === null && state.cursor < queue.length) {
          const item = queue[state.cursor];
          state.cursor += 1;
          patchItem(item.key, { status: "sending", error: undefined });

          const outcome = await sendExcursionSMS(item.excursionId, {
            user_ids: item.recipient.kind === "user" ? [item.recipient.id] : [],
            customer_ids: item.recipient.kind === "customer" ? [item.recipient.id] : [],
            ...payload,
          });

          if (outcome.ok) {
            patchItem(item.key, outcomeFromResult(outcome.result));
            continue;
          }

          patchItem(item.key, { status: "failed", error: outcome.error });
          if (isGlobalErrorCode(outcome.code)) state.abortError = outcome.error;
        }
      };

      const workerCount = Math.min(SMS_DISPATCH_CONCURRENCY, queue.length);
      await Promise.all(Array.from({ length: workerCount }, () => worker()));

      const abortError = state.abortError;
      if (abortError !== null) {
        const queuedKeys = new Set(queue.map((it) => it.key));
        setItems((prev) =>
          prev.map((it) =>
            queuedKeys.has(it.key) && it.status === "pending"
              ? { ...it, status: "cancelled", error: abortError }
              : it,
          ),
        );
        setGlobalError(abortError);
      }

      runningRef.current = false;
      setRunning(false);
    },
    [patchItem, sendExcursionSMS],
  );

  const start = React.useCallback(
    async (queue: SmsDispatchItem[], payload: SmsDispatchPayload) => {
      if (runningRef.current) return;
      payloadRef.current = payload;
      setItems(queue);
      await run(queue, payload);
    },
    [run],
  );

  const retryFailed = React.useCallback(async () => {
    if (runningRef.current) return;
    const queue = items
      .filter((it) => isRetryableSmsStatus(it.status))
      .map((it) => ({ ...it, status: "pending" as const, error: undefined }));
    if (queue.length === 0) return;
    const retryKeys = new Set(queue.map((it) => it.key));
    setItems((prev) =>
      prev.map((it) =>
        retryKeys.has(it.key) ? { ...it, status: "pending", error: undefined } : it,
      ),
    );
    await run(queue, payloadRef.current);
  }, [items, run]);

  const reset = React.useCallback(() => {
    if (runningRef.current) return;
    setItems([]);
    setGlobalError(null);
  }, []);

  const counters = React.useMemo<SmsDispatchCounters>(() => {
    const next: SmsDispatchCounters = {
      total: items.length,
      done: 0,
      sent: 0,
      refused: 0,
      failed: 0,
      retryable: 0,
    };
    for (const it of items) {
      if (it.status !== "pending" && it.status !== "sending") next.done += 1;
      if (SENT_STATUSES.has(it.status)) next.sent += 1;
      if (REFUSED_STATUSES.has(it.status)) next.refused += 1;
      if (isRetryableSmsStatus(it.status)) {
        next.failed += 1;
        next.retryable += 1;
      }
    }

    return next;
  }, [items]);

  return { items, running, globalError, counters, start, retryFailed, reset };
}
